/**
 * 金币收集动画UI - 管理+5文字掉落动画效果
 */

import * as THREE from 'three';

export class CoinAnimationUI {
    constructor(game) {
        this.game = game;
        this.animations = new Map(); // 存储活跃的动画
        this.animationCounter = 0; // 动画ID计数器

        // 创建UI容器
        this.createUIContainer();

        // 临时向量用于坐标转换
        this.tempVector = new THREE.Vector3();
    }

    createUIContainer() {
        // 创建金币动画UI的容器元素
        this.uiContainer = document.createElement('div');
        this.uiContainer.id = 'coin-animation-container';
        this.uiContainer.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 200;
        `;

        document.body.appendChild(this.uiContainer);
    }

    /**
     * 创建金币收集动画
     * @param {THREE.Vector3} worldPosition - 玩家船只的世界坐标
     * @param {number} coinValue - 收集到的金币数值
     */
    createCoinAnimation(worldPosition, coinValue) {
        const animationId = ++this.animationCounter;

        // 创建金币动画容器
        const coinContainer = document.createElement('div');
        coinContainer.className = 'coin-animation';
        coinContainer.style.cssText = `
            position: absolute;
            display: flex;
            align-items: center;
            gap: 8px;
            opacity: 1;
            transform: translate(-50%, -50%);
            transition: none;
            z-index: 201;
        `;

        // 创建金币图标
        const coinIcon = document.createElement('div');
        coinIcon.style.cssText = `
            width: 28px;
            height: 28px;
            background: radial-gradient(circle at 30% 30%, #FFEF94, #FFD700, #B8860B);
            border: 3px solid #FFA500;
            border-radius: 50%;
            position: relative;
            box-shadow:
                inset 2px 2px 4px rgba(255, 255, 255, 0.3),
                inset -2px -2px 4px rgba(0, 0, 0, 0.2),
                0 4px 8px rgba(0, 0, 0, 0.3);
        `;

        // 添加金币内部图案
        const coinInner = document.createElement('div');
        coinInner.style.cssText = `
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 16px;
            height: 16px;
            background: radial-gradient(circle, #FFF8DC, #FFD700);
            border-radius: 50%;
            border: 1px solid #B8860B;
        `;
        coinIcon.appendChild(coinInner);

        // 创建+数字文字
        const coinText = document.createElement('div');
        coinText.textContent = `+${coinValue}`;
        coinText.style.cssText = `
            color: #FFD700;
            font-family: Arial, sans-serif;
            font-size: 24px;
            font-weight: bold;
            text-shadow:
                2px 2px 4px rgba(0,0,0,0.8),
                0 0 8px rgba(255, 215, 0, 0.6);
            margin: 0;
        `;

        // 组装容器 - 数字在前，金币图标在后
        coinContainer.appendChild(coinText);
        coinContainer.appendChild(coinIcon);

        this.uiContainer.appendChild(coinContainer);

        // 存储动画状态
        const animation = {
            element: coinContainer,
            startTime: Date.now(),
            duration: 2000, // 2秒动画
            worldPosition: worldPosition.clone(),
            startY: 0, // 将在第一次更新时设置
            endY: 0,   // 将在第一次更新时设置
            initialized: false
        };

        this.animations.set(animationId, animation);

        // 设置自动清理
        setTimeout(() => {
            this.removeAnimation(animationId);
        }, animation.duration + 100);

        return animationId;
    }

    /**
     * 更新所有活跃的动画
     */
    update() {
        const currentTime = Date.now();

        this.animations.forEach((animation, animationId) => {
            if (!animation.initialized) {
                // 初始化动画位置
                const screenPos = this.worldToScreen(animation.worldPosition);
                if (screenPos) {
                    animation.startY = screenPos.y - 40; // 在船头上方40像素开始
                    animation.endY = screenPos.y + 20;   // 掉落到船体内部20像素
                    animation.initialized = true;
                }
            }

            if (!animation.initialized) return;

            const elapsed = currentTime - animation.startTime;
            const progress = Math.min(elapsed / animation.duration, 1);

            // 更新屏幕位置
            const screenPos = this.worldToScreen(animation.worldPosition);
            if (!screenPos) {
                // 如果不在屏幕内，隐藏动画
                animation.element.style.opacity = '0';
                return;
            }

            // 计算动画位置 - 从船头上方掉落到船体内
            const currentY = animation.startY + (animation.endY - animation.startY) * this.easeInQuad(progress);

            // 更新位置
            animation.element.style.left = `${screenPos.x}px`;
            animation.element.style.top = `${currentY}px`;

            // 透明度动画 - 开始时完全不透明，最后0.5秒逐渐消失
            let opacity = 1;
            if (progress > 0.75) {
                opacity = 1 - ((progress - 0.75) / 0.25);
            }
            animation.element.style.opacity = opacity;

            // 缩放动画 - 略微放大然后恢复
            const scale = 1 + Math.sin(progress * Math.PI) * 0.2;

            // 金币旋转动画
            const coinIcon = animation.element.children[1]; // 第二个元素是金币图标
            if (coinIcon) {
                const rotation = progress * 360; // 完整旋转一圈
                coinIcon.style.transform = `rotateY(${rotation}deg)`;
            }

            animation.element.style.transform = `translate(-50%, -50%) scale(${scale})`;

            // 完成动画
            if (progress >= 1) {
                this.removeAnimation(animationId);
            }
        });
    }

    /**
     * 缓动函数 - 二次缓入
     */
    easeInQuad(t) {
        return t * t;
    }

    /**
     * 将3D世界坐标转换为2D屏幕坐标
     */
    worldToScreen(worldPosition) {
        if (!this.game.sceneManager.camera) {
            return null;
        }

        // 复制世界位置
        this.tempVector.copy(worldPosition);
        this.tempVector.y += 3; // 在船只上方显示

        // 投影到屏幕坐标
        this.tempVector.project(this.game.sceneManager.camera);

        // 检查是否在屏幕内
        if (this.tempVector.z > 1 || this.tempVector.z < 0) {
            return null;
        }

        // 转换为屏幕像素坐标
        const screenX = (this.tempVector.x * 0.5 + 0.5) * window.innerWidth;
        const screenY = (-this.tempVector.y * 0.5 + 0.5) * window.innerHeight;

        return {
            x: screenX,
            y: screenY,
            z: this.tempVector.z
        };
    }

    /**
     * 移除动画
     */
    removeAnimation(animationId) {
        const animation = this.animations.get(animationId);
        if (animation) {
            if (animation.element && animation.element.parentNode) {
                animation.element.parentNode.removeChild(animation.element);
            }
            this.animations.delete(animationId);
        }
    }

    /**
     * 清理所有动画
     */
    dispose() {
        this.animations.forEach((animation, animationId) => {
            this.removeAnimation(animationId);
        });

        if (this.uiContainer && this.uiContainer.parentNode) {
            this.uiContainer.parentNode.removeChild(this.uiContainer);
        }
    }
}
